"use client"

import React, { useEffect } from "react"
import {
  fetchAddressesForCustomerAction,
  loadBasketLineItemsAction,
  loadBasketPaymentsAction,
  loadBasketPromotionCodesAction,
  loadCurrentBasketAction,
} from "@/actions/basketActions"
import { useUbStorefrontState } from "@/store/ubStorefrontState"

import { BasketContext } from "@/lib/storefront/basket/basket"

interface BasketLoaderClientProps {
  basketContext: BasketContext
}

const SIMULATE_BASKET_LOAD_ERROR_STORAGE_KEY = "ubSimulateBasketLoadError"

function shouldSimulateBasketLoadError() {
  return typeof window !== "undefined" && window.localStorage.getItem(SIMULATE_BASKET_LOAD_ERROR_STORAGE_KEY) === "true"
}

export default function BasketLoaderClient(props: BasketLoaderClientProps) {
  const isoLocale = props.basketContext.isoLocale
  const isB2B = props.basketContext.isB2B
  const userData = props.basketContext.userData
  const customerData = props.basketContext.customerData

  const {
    basketData,
    setBasketData,
    setBasketLoadError,
    setBasketLineItems,
    setBasketPromotionCodes,
    setBasketPayments,
    addresses,
    setAddresses,
  } = useUbStorefrontState()

  useEffect(() => {
    if (shouldSimulateBasketLoadError()) {
      setBasketLoadError(true)
      return
    }

    if (basketData) {
      loadBasketLineItemsAction(isoLocale, isB2B, basketData)
        .then((lineItems) => {
          if (lineItems) {
            lineItems.sort((a, b) => (a.position > b.position ? 1 : -1))
            //console.dir(lineItems, {depth: 9})

            setBasketLineItems(lineItems)
            setBasketLoadError(false)
          }
        })
        .catch((error) => {
          console.error("Could not load basket line items", error)
          setBasketLoadError(true)
        })
    } else {
      loadCurrentBasketAction()
        .then((loadedBasketData) => {
          if (loadedBasketData) {
            setBasketData(loadedBasketData)
            setBasketLoadError(false)
          }
        })
        .catch((error) => {
          console.error("Could not load current basket", error)
          setBasketLoadError(true)
        })
    }
  }, [basketData, isB2B, isoLocale, setBasketData, setBasketLineItems, setBasketLoadError, userData])

  useEffect(() => {
    if (basketData) {
      loadBasketPromotionCodesAction()
        .then((promotionCodes) => {
          if (promotionCodes) {
            setBasketPromotionCodes(promotionCodes)
          }
        })
        .catch((error) => {
          console.error("Could not load basket promotion codes", error)
        })
    }
  }, [basketData, isB2B, setBasketPromotionCodes])

  useEffect(() => {
    if (basketData) {
      loadBasketPaymentsAction()
        .then((payments) => {
          if (payments) {
            setBasketPayments(payments)
          }
        })
        .catch((error) => {
          console.error("Could not load basket payments", error)
        })
    }
  }, [basketData, isB2B, setBasketPayments])

  useEffect(() => {
    if (!addresses) {
      if (customerData && customerData?.customerNo) {
        fetchAddressesForCustomerAction(customerData.customerNo).then((addresses) => {
          if (addresses) {
            setAddresses(addresses)
          } else {
            setAddresses([])
          }
        })
      }
    }
  }, [addresses, customerData, setAddresses])

  return <></>
}
